// services/api.ts - TypeScript API Service
import axios, { AxiosResponse } from 'axios';

export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type Role = 'user' | 'formateur' | 'admin';

// Axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

// Interceptors for token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle expired tokens
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      localStorage.removeItem('token');
      window.location.href = '/signin';
    }
    return Promise.reject(error);
  }
);


interface SocialMedia {
  facebook?: string | null;
  instagram?: string | null;
  linkedin?: string | null;
}

// Types
interface AuthResponse {
  success: boolean;
  message: string;
  token: string;
  user: {
    id: number;
    email: string;
    firstName: string;
    lastName: string;
    role: Role;
    emailConfirmed: boolean;
    provider?: 'local' | 'google';
    photo?: string;
    phone?: string | null;
    address?: string | null;
    bio?: string | null;
    socialMedia?: SocialMedia | null;
  };
}


interface UserResponse {
  user: AuthResponse['user'];
}

interface SignupData {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

interface SigninData {
  email: string;
  password: string;
}

// Main API object
export const authAPI = {
  signup: (userData: SignupData): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/api/auth/signup', userData),

  signin: (credentials: SigninData): Promise<AxiosResponse<AuthResponse>> =>
    api.post('/api/auth/signin', credentials),

  logout: (): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/api/auth/logout'),

  getCurrentUser: (): Promise<AxiosResponse<UserResponse>> =>
    api.get('/api/auth/me'),

  // Update profile (corrected route)
  updateProfile: (profileData: FormData | {
    firstName?: string;
    lastName?: string;
    email?: string;
    phone?: string;
    address?: string;
    bio?: string;
    photo?: string;
    facebook?: string;
    instagram?: string;
    linkedin?: string;
  }): Promise<AxiosResponse<{ success: boolean; user: AuthResponse['user'] }>> => {
    const config = profileData instanceof FormData
      ? { headers: { 'Content-Type': 'multipart/form-data' } }
      : {};

    return api.put('/api/user/profile', profileData, config);
  },

  // Change password (corrected route + correct field names)
  changePassword: (data: {
    oldPassword: string;
    newPassword: string;
  }): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.put('/api/user/password', data),

  // Forgot password (send reset link)
  forgotPassword: (email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/api/auth/forgot-password', { email }),

  // Reset password (with token + new password)
  resetPassword: (data: {
    token: string;
    newPassword: string;
  }): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/api/auth/reset-password', data),

  // Email verification for signup
  sendVerificationEmail: (email: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/api/auth/send-verification-email', { email }),

  // Verify email with token
  verifyEmail: (token: string): Promise<AxiosResponse<{ message: string }>> =>
    api.post('/api/auth/verify-email', { token }),

  getGoogleAuthUrl: (): string => `${API_BASE_URL}/api/auth/google`,
  getFacebookAuthUrl: (): string => `${API_BASE_URL}/api/auth/facebook`,
  getLinkedInAuthUrl: (): string => `${API_BASE_URL}/api/auth/linkedin`,

  };

// Formations
export const formationAPI = {
  getAll: () => api.get('/api/formations'),
  getLastThree: () => api.get('/api/formations/last-three'),
  getSimilarFormations: (id: number) => api.get(`/api/formations/similar/${id}`),
  getFormationDetails: (id: number) => api.get(`/api/formations/details/${id}`),
  getFormationProgress: (id: number) => api.get(`/api/formations/formation-progress/${id}`),
  getCompletedFormations: (id: number) => api.get(`/api/formations/completed-formations/${id}`),
  completeChapitre: (data: {
    userId: number
    formationId: number
    chapitreId: number
  }) => api.post('/api/formations/complete-chapitre', data),
  getWatchFormation: (formationId: number, userId: number) =>
    api.get(`/api/formations/watch/${formationId}`, {
      params: { userId }
    }),
  getWatchContent: (formationId: number) =>
    api.get(`/api/formations/watch-content/${formationId}`),
  resetFormationProgress: (data: { userId: number; formationId: number }) =>
  api.post('/api/formations/reset-formation-progress', data),

  getById: (id: number) => api.get(`/api/formations/${id}`),
  create: (data: any) => api.post('/api/formations', data),
  update: (id: number, data: any) => api.put(`/api/formations/${id}`, data),
  delete: (id: number) => api.delete(`/api/formations/${id}`),
};

// Formation Categories
export const categoryAPI = {
  getAll: () => api.get('/api/categories'),
  getById: (id: number) => api.get(`/api/categories/${id}`),
  create: (data: any) => api.post('/api/categories', data),
  update: (id: number, data: any) => api.put(`/api/categories/${id}`, data),
  delete: (id: number) => api.delete(`/api/categories/${id}`),
};

// Formation Sections
export const sectionAPI = {
  getAll: () => api.get('/api/sections'),
  getById: (id: number) => api.get(`/api/sections/${id}`),
  create: (data: any) => api.post('/api/sections', data),
  update: (id: number, data: any) => api.put(`/api/sections/${id}`, data),
  delete: (id: number) => api.delete(`/api/sections/${id}`),
};

// Formation Chapitres
export const chapitreAPI = {
  getAll: () => api.get('/api/chapitres'),
  getById: (id: number) => api.get(`/api/chapitres/${id}`),
  create: (data: any) => api.post('/api/chapitres', data),
  update: (id: number, data: any) => api.put(`/api/chapitres/${id}`, data),
  delete: (id: number) => api.delete(`/api/chapitres/${id}`),
};

// Formation Public Cible
export const publicCibleAPI = {
  getAll: () => api.get('/api/public-cible'),
  getById: (id: number) => api.get(`/api/public-cible/${id}`),
  create: (data: any) => api.post('/api/public-cible', data),
  update: (id: number, data: any) => api.put(`/api/public-cible/${id}`, data),
  delete: (id: number) => api.delete(`/api/public-cible/${id}`),
};

// Formation Apprendres
export const apprendreAPI = {
  getAll: () => api.get('/api/apprendre'),
  getById: (id: number) => api.get(`/api/apprendre/${id}`),
  create: (data: any) => api.post('/api/apprendre', data),
  update: (id: number, data: any) => api.put(`/api/apprendre/${id}`, data),
  delete: (id: number) => api.delete(`/api/apprendre/${id}`),
};

// Formation Ressources
export const ressourceAPI = {
  getAll: () => api.get('/api/ressources'),
  getById: (id: number) => api.get(`/api/ressources/${id}`),
  create: (data: any) => api.post('/api/ressources', data),
  update: (id: number, data: any) => api.put(`/api/ressources/${id}`, data),
  delete: (id: number) => api.delete(`/api/ressources/${id}`),
};

// Inscriptions
export const inscriptionAPI = {
  getUserRequests: (id: number) => api.get(`/api/inscriptions/getUserRequests/${id}`),
  getAll: () => api.get('/api/inscriptions'),
  getById: (id: number) => api.get(`/api/inscriptions/${id}`),
  create: (data: any) => api.post('/api/inscriptions', data),
  update: (id: number, data: any) => api.put(`/api/inscriptions/${id}`, data),
  delete: (id: number) => api.delete(`/api/inscriptions/${id}`),
};

// Contact Us
export const contactAPI = {
  sendMessage: (data: {
    firstName: string;
    lastName: string;
    email: string;
    subject: string;
    message: string;
  }) => api.post('/api/contact', data),
};


// Admin Dashboard
export const adminDashboard = {
    // Get counts of users, formations, inscriptions with stats
    getCounts: () => api.get('/api/admin/dashboard/counts'),

    // Get inscriptions with status = 'En attente'
    getPendingInscriptions: () => api.get('/api/admin/inscriptions/pending'),

    // Approve an inscription by ID
    approveInscription: (id: number) => api.post(`/api/admin/inscriptions/${id}/approve`),

    // Get all users with formation counts
    getAllUsers: () => api.get('/api/admin/users/all'),

    // Ban or delete a user
    updateUserAction: (userId: number, action: 'ban' | 'unban' | 'delete') =>
      api.post(`/api/admin/users/${userId}/action`, { action }),

    // Change user role (admin only)
    changeUserRole: (userId: number, role: 'user' | 'admin' | 'formateur') =>
      api.put(`/api/admin/users/${userId}/role`, { role }),

    // Get user details with stats
    getUserDetails: (userId: number) =>
      api.get(`/api/admin/users/${userId}/details`),

    // Get user's approved formations with progress
    getUserFormations: (userId: number) =>
      api.get(`/api/admin/users/${userId}/formations`),

    // GET all inscriptions with formation name + all formations list
    getAllInscriptions: () => api.get('/api/admin/inscriptions/all'),

    // Approve or reject an inscription (only if status is "En attente")
    approveOrRejectInscription: (id: number, action: 'approuve' | 'reject') =>
  api.post(`/api/admin/inscriptions/${id}/action`, { action }),

    getInscriptionFormData: () => api.get('/api/admin/inscriptions/form-data'),

    createInscription: (userId: number, formationId: number) =>
  api.post('/api/admin/inscriptions', { userId, formationId }),

    // Get all formations with stats (overview)
    getFormationsOverview: () => api.get('/api/admin/formations/overview'),

    // Delete a formation by ID
    deleteFormation: (id: number) => api.delete(`/api/admin/formations/${id}`),

    // get all formation content for edit
      getFormationForEdit: (id: number) =>
    api.get(`/api/admin/formations/${id}/edit`),

    // update a formation by ID
    updateFormation: (id: number, formData: FormData) =>
    api.put(`/api/admin/formations/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),

    // Create new formation
    createFormation: (formData: FormData) =>
    api.post('/api/admin/formations', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }),
};


export const formateurDashboard = {

    // Get all formateur formations with stats (overview)
    getFormateurFormationsOverview: (formateurId: number) => api.get(`/api/formateur/formations/overview/${formateurId}`),

};

export default api;
